import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Instagram, Music2, Youtube, Facebook, Twitch, Linkedin, Globe } from "lucide-react";
import { api } from "@/lib/api";
import EmbedCard from "@/components/EmbedCard";
import { WORK_CATEGORIES } from "@/lib/embeds";

const SOCIALS = [
  { key: "instagram", Icon: Instagram, label: "Instagram" },
  { key: "tiktok", Icon: Music2, label: "TikTok" },
  { key: "youtube", Icon: Youtube, label: "YouTube" },
  { key: "facebook", Icon: Facebook, label: "Facebook" },
  { key: "twitch", Icon: Twitch, label: "Twitch" },
  { key: "linkedin", Icon: Linkedin, label: "LinkedIn" },
  { key: "web", Icon: Globe, label: "Web" },
];

function fmt(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n;
}

export default function TalentDetailPage() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [works, setWorks] = useState([]);
  const [filterCat, setFilterCat] = useState("Todas");
  const [filterBrand, setFilterBrand] = useState("Todas");

  useEffect(() => { api.get(`/talents/${id}`).then(setT).catch(() => setT(null)); }, [id]);
  useEffect(() => { api.get(`/talents/${id}/works`).then(setWorks).catch(() => setWorks([])); }, [id]);

  const brands = useMemo(() => ["Todas", ...Array.from(new Set(works.map((w) => w.brand).filter(Boolean)))], [works]);
  const filteredWorks = works.filter((w) => (filterCat === "Todas" || w.category === filterCat) && (filterBrand === "Todas" || w.brand === filterBrand));

  if (!t) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Cargando...</div>;

  const stats = [
    { label: "Instagram", value: fmt(t.followers_instagram) },
    { label: "TikTok", value: fmt(t.followers_tiktok) },
    { label: "YouTube", value: fmt(t.followers_youtube) },
    { label: "Facebook", value: fmt(t.followers_facebook) },
    { label: "Engagement", value: t.engagement ? `${t.engagement}%` : "—" },
    { label: "Alcance", value: fmt(t.reach) },
  ];

  return (
    <div data-testid="talent-detail-page" className="pt-12 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Link to="/talentos" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="talent-back">
          <ArrowLeft className="w-4 h-4" /> Volver a talentos
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-5">
            <div className="aspect-[3/4] rounded-sm overflow-hidden border border-border bg-secondary">
              {t.photo && <img src={t.photo} alt={t.artist_name} className="w-full h-full object-cover" />}
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-2 mb-4">
              {(t.category || "").split("/").map((c, i) => (
                <span key={i} className="bg-primary/10 text-primary text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-sm">{c.trim()}</span>
              ))}
            </div>
            <h1 className="font-display text-5xl lg:text-7xl font-black tracking-tighter mb-3" data-testid="talent-name">{t.artist_name}</h1>
            <p className="flex items-center gap-2 text-muted-foreground mb-6"><MapPin className="w-4 h-4" /> {t.city}, {t.country}</p>
            <p className="text-base lg:text-lg leading-relaxed mb-6 text-balance">{t.short_description}</p>
            {t.bio && <p className="text-sm text-muted-foreground leading-relaxed mb-8">{t.bio}</p>}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border border border-border rounded-sm overflow-hidden mb-8">
              {stats.map((s, i) => (
                <div key={i} className="bg-background p-5">
                  <div className="font-display text-2xl font-black tracking-tighter">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {SOCIALS.filter((s) => t[s.key]).map(({ key, Icon, label }) => (
                <a key={key} href={t[key]} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-sm text-sm font-semibold hover:border-primary hover:text-primary transition-colors">
                  <Icon className="w-4 h-4" /> {label}
                </a>
              ))}
            </div>

            <Link to="/contacto" data-testid="talent-cta" className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary text-primary-foreground font-semibold rounded-sm hover:opacity-90">
              Solicitar campaña con {t.artist_name}
            </Link>
          </div>
        </div>

        {works.length > 0 && (
          <section className="mt-20" data-testid="talent-works">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-8 gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3 font-bold">Trabajos & Campañas</p>
                <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tighter">Lo que hace {t.artist_name.split(" ")[0]} con marcas.</h2>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-8">
              {["Todas", ...WORK_CATEGORIES].map((c) => (
                <button key={c} onClick={() => setFilterCat(c)} className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors ${filterCat === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`} data-testid={`works-filter-${c}`}>
                  {c}
                </button>
              ))}
            </div>
            {brands.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {brands.map((b) => (
                  <button key={b} onClick={() => setFilterBrand(b)} className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors ${filterBrand === b ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>
                    {b}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredWorks.map((w) => <EmbedCard key={w.id} work={w} />)}
            </div>
            {filteredWorks.length === 0 && <p className="text-muted-foreground py-12 text-center">Sin publicaciones para este filtro.</p>}
          </section>
        )}
      </div>
    </div>
  );
}
